import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, getAuthToken } from "@/lib/api";

/**
 * Iter 77 · EDI Notification Modal + global WebSocket listener.
 *
 * Subscribes to the existing `/api/ws/chat` channel (re-used for the
 * tenant pub/sub) and watches for `type: "edi_received"` frames. Pops
 * up a bilingual modal with Supplier / Invoice / Total preview and
 * two action buttons: APPROVE & SYNC (creates invoice + cheque rows)
 * or REJECT (deletes envelope). Toasts on success.
 *
 * Mounted once at the KioskShell layer so every authenticated route
 * inherits the listener without duplicating sockets.
 */
export default function EdiNotificationModal() {
  const [envelope, setEnvelope] = useState(null);
  const [busy, setBusy] = useState(false);
  const wsRef = useRef(null);

  // ── Global WebSocket subscriber (mirrors Chat.jsx connection logic)
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const jwt = getAuthToken() || localStorage.getItem("kf_jwt") || "";
    const url = jwt
      ? `${base}/api/ws/chat?auth=${encodeURIComponent(jwt)}`
      : `${base}/api/ws/chat?applock=savvaspos:store`;

    let alive = true;
    let backoff = 1000;
    function connect() {
      if (!alive) return;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => { backoff = 1000; };
      ws.onmessage = (ev) => {
        try {
          const frame = JSON.parse(ev.data);
          if (frame?.type === "edi_received" && frame.envelope) {
            setEnvelope(frame.envelope);
          }
        } catch { /* malformed frame */ }
      };
      ws.onclose = () => {
        if (alive) {
          setTimeout(connect, backoff);
          backoff = Math.min(backoff * 2, 15000);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    }
    connect();
    return () => {
      alive = false;
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, []);

  const close = () => { if (!busy) setEnvelope(null); };

  const handleApprove = async () => {
    if (!envelope?.id) return;
    setBusy(true);
    try {
      await api.post(`/edi/${envelope.id}/approve`);
      toast.success("EDI εγκρίθηκε / Approved & synced");
      setEnvelope(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!envelope?.id) return;
    setBusy(true);
    try {
      await api.post(`/edi/${envelope.id}/reject`);
      toast.success("EDI απορρίφθηκε / Rejected");
      setEnvelope(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  if (!envelope) return null;

  const total = Number(envelope.total_amount || 0).toFixed(2);

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent
        data-testid="edi-modal"
        className="max-w-lg border-2 border-amber-400 bg-gradient-to-b from-slate-900 to-slate-950 text-slate-100 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-500/20 p-3 ring-2 ring-amber-400/40">
            <Inbox className="h-6 w-6 text-amber-300" />
          </div>
          <div className="flex-1">
            <DialogTitle
              data-testid="edi-modal-title"
              className="text-base font-bold leading-tight text-amber-200"
            >
              📥 ΛΗΦΘΗΚΕ ΗΛΕΚΤΡΟΝΙΚΟ ΤΙΜΟΛΟΓΙΟ (EDI)!
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400 mt-0.5">
              Electronic invoice received · Review and decide
            </DialogDescription>
          </div>
        </div>

        <div
          data-testid="edi-modal-body"
          className="mt-4 space-y-2 rounded-lg bg-slate-800/60 p-4 ring-1 ring-slate-700"
        >
          <Row label="Supplier · Προμηθευτής" value={envelope.supplier_name || "—"} testid="edi-supplier" />
          <Row label="Invoice # · Αριθμός" value={envelope.invoice_number || "—"} testid="edi-invoice-number" />
          <Row
            label="Total · Σύνολο"
            value={`€${total}`}
            testid="edi-total"
            valueClass="text-emerald-300 font-bold"
          />
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <Button
            data-testid="edi-approve-btn"
            disabled={busy}
            onClick={handleApprove}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-6"
          >
            <CheckCircle2 className="h-5 w-5 mr-2" />
            🔗 ΕΓΚΡΙΣΗ & ΕΝΗΜΕΡΩΣΗ / APPROVE & SYNC
          </Button>
          <Button
            data-testid="edi-reject-btn"
            disabled={busy}
            onClick={handleReject}
            variant="destructive"
            className="flex-1 py-6 font-semibold"
          >
            <XCircle className="h-5 w-5 mr-2" />
            ❌ ΑΠΟΡΡΙΨΗ / REJECT
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, testid, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
      <span data-testid={testid} className={`text-sm text-slate-100 ${valueClass}`}>
        {value}
      </span>
    </div>
  );
}
